import React from 'react';
import { StandardTableActions, Select, Badge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { Call } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { RatingView } from '../../components/ui/Rating';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';

// Component for rating display with theme colors
const RatingDisplay: React.FC<{ value: number }> = ({ value }) => {
  const { colors } = useTheme();
  return (
    <div className="flex items-center gap-1">
      <RatingView value={value} precision={0.5} max={5} size="sm" />
      <span className={`text-xs ${colors.textSecondary} ml-1`}>({value}/5)</span>
    </div>
  );
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Calls',
    description: 'Manage your client calls database',
    permissionModuleKey: 'calls',
    route: {
      list: '/calls',
      new: '/calls/new',
      detail: (id: number) => `/calls/${id}`,
      edit: (id: number) => `/calls/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-calls',
      detail: (id: number) => `/crm-clients-calls/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search calls by client, user, or description...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientId', 'calledBy', 'clientCallTypeId', 'clientCallInterested', 'clientCallSendEmail'
      ],
    },
    {
      name: 'details',
      label: 'Details',
      icon: '📋',
      fields: [
        'clientCallDate', 'clientCallDescr', 'clientCallNotes', 'clientColor'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Call) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'calledBy',
        label: 'Called By',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: Call) => 
          item.calledByUser
            ? (item.calledByUser.userDisplayName || item.calledByUser.userFirstName || `User #${item.calledByUser.id}`)
            : (item.calledBy ? `User #${item.calledBy}` : ''),
        options: [],
      },
      {
        key: 'clientCallTypeId',
        label: 'Call Type',
        placeholder: 'All Call Types',
        type: 'select' as const,
        getValue: (item: Call) => 
          item.clientCallType?.call_type_desc || (item.clientCallTypeId ? `Call Type #${item.clientCallTypeId}` : ''),
        options: [],
      },
      {
        key: 'clientCallDate',
        label: 'Call Date',
        placeholder: 'All Call Date',
        type: 'date' as const,
        getValue: (item: Call) =>
          item.clientCallDate
            ? formatDateGlobal(item.clientCallDate)
            : undefined,
        options: [],
      },
      {
        key: 'clientCallDescr',
        label: 'Description',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Call) => item.clientCallDescr,
        options: [],
      },
      {
        key: 'clientCallNotes',
        label: 'Notes',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Call) => item.clientCallNotes,
        options: [],
      },
      {
        key: 'clientCallInterested',
        label: 'Interest Level',
        placeholder: 'All Interest Levels',
        type: 'number' as const,
        getValue: (item: Call) => item.clientCallInterested?.toString(),
        options: [],
      },
      {
        key: 'clientCallSendEmail',
        label: 'Send Email',
        placeholder: 'All Send Email',
        type: 'boolean' as const,
        getValue: (item: Call) => item.clientCallSendEmail ? 'Yes' : 'No',
        options: ['Yes', 'No'],
      },
      {
        key: 'clientColor',
        label: 'Color',
        placeholder: 'All Colors',
        getValue: (item: Call) => item.clientColor,
        options: [],
      },
    ],
    applyFilter: (
      items: Call[],
      filters: {
        clientId: string[];
        calledBy: string[];
        clientCallTypeId: string[];
        clientCallDate: string[];
        clientCallDescr: string[] | string;
        clientCallNotes: string[] | string;
        clientCallInterested: string[];
        clientCallSendEmail: string[];
        clientColor: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client filter (matches by display name or ID)
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Called By filter (matches by display name or ID)
        if (filters.calledBy && filters.calledBy.length > 0) {
          const displayValue = item.calledByUser
            ? (item.calledByUser.userDisplayName || item.calledByUser.userFirstName || `User #${item.calledByUser.id}`)
            : (item.calledBy ? `User #${item.calledBy}` : '');
          const matches = filters.calledBy.some((filter) => {
            return displayValue === filter ||
                   (item.calledBy && filter === `User #${item.calledBy}`) ||
                   (item.calledByUser?.userDisplayName && filter === item.calledByUser.userDisplayName) ||
                   (item.calledByUser?.id && filter === `User #${item.calledByUser.id}`);
          });
          if (!matches) return false;
        }

        // Call Type filter (matches by description or ID)
        if (filters.clientCallTypeId && filters.clientCallTypeId.length > 0) {
          const displayValue = item.clientCallType?.call_type_desc || (item.clientCallTypeId ? `Call Type #${item.clientCallTypeId}` : '');
          const matches = filters.clientCallTypeId.some((filter) => {
            return displayValue === filter ||
                   (item.clientCallTypeId && filter === `Call Type #${item.clientCallTypeId}`) ||
                   (item.clientCallType?.call_type_desc && filter === item.clientCallType.call_type_desc);
          });
          if (!matches) return false;
        }

        // Call Date filter
        if (filters.clientCallDate.length > 0) {
          if (ranges.clientCallDate) {
            const range = ranges.clientCallDate;
            if (range.start && range.end && item.clientCallDate) {
              const itemDate = new Date(item.clientCallDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientCallDate
              ? formatDateGlobal(item.clientCallDate)
              : '';
            const matches = filters.clientCallDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Description filter (text)
        const descrFilter = filters.clientCallDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.clientCallDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.clientCallDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Notes filter (text)
        const notesFilter = filters.clientCallNotes;
        if (typeof notesFilter === 'string' && notesFilter.trim() !== '') {
          const value = (item.clientCallNotes || '').toLowerCase();
          if (!value.includes(notesFilter.toLowerCase())) return false;
        } else if (Array.isArray(notesFilter) && notesFilter.length > 0) {
          const value = (item.clientCallNotes || '').toLowerCase();
          const matches = notesFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Interest Level filter
        if (filters.clientCallInterested && filters.clientCallInterested.length > 0) {
          const value = item.clientCallInterested?.toString() || '';
          const matches = filters.clientCallInterested.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Send Email filter
        if (filters.clientCallSendEmail && filters.clientCallSendEmail.length > 0) {
          const sendEmail = item.clientCallSendEmail ? 'Yes' : 'No';
          if (!filters.clientCallSendEmail.includes(sendEmail)) return false;
        }

        // Color filter
        if (filters.clientColor && filters.clientColor.length > 0) {
          const value = item.clientColor || '';
          const matches = filters.clientColor.some((filter) => {
            return value.toLowerCase().includes(filter.toLowerCase()) || filter.toLowerCase().includes(value.toLowerCase());
          });
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Call',
        showTitle: false,
        variant: 'primary' as const,
        href: '/calls/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Call) => {
      window.location.href = `/calls/${item.clientCallId}`;
    },
    keyExtractor: (item: Call) => item.clientCallId,
    getClientId: (item: Call) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new call.',
    noSearchResults: 'No calls found matching your search criteria.',
    noFilterResults: 'No calls match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Call',
    message: 'Are you sure you want to delete this call? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete call. Please try again.',
    handler: async (id: number) => {
      const { deleteCall } = await import('../../services/mutations');
      await deleteCall(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: Call) => item.clientCallId,
    getTitle: (item: Call) => {
      const truncated = item.clientCallDescr 
        ? (item.clientCallDescr.length > 60 ? item.clientCallDescr.substring(0, 60) + '...' : item.clientCallDescr)
        : `Call #${item.clientCallId}`;
      return truncated;
    },
    getSubtitle: (item: Call) => {
      if (item.client?.clientName) {
        return `Client: ${item.client.clientName}`;
      }
      return '';
    },
    getStatusBadge: (item: Call) => {
      if (item.clientCallSendEmail) {
        return {
          label: 'Email Sent',
          variant: 'success' as const,
        };
      }
      return undefined;
    },
    getCardFields: (item: Call) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        const clientName = item.client.clientName;
        const truncated = clientName.length > 50 ? clientName.substring(0, 50) + '...' : clientName;
        fields.push({
          value: truncated,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Called By User
      if (item.calledByUser) {
        const calledByName = item.calledByUser.userDisplayName || item.calledByUser.userFirstName || `User #${item.calledByUser.id}`;
        fields.push({
          label: 'Called By',
          value: calledByName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Call Type
      if (item.clientCallType?.call_type_desc) {
        const callType = item.clientCallType.call_type_desc;
        const isOutgoing = callType.toLowerCase().includes('outgoing');
        fields.push({
          value: callType,
          badge: true,
          badgeVariant: isOutgoing ? 'success' as const : 'info' as const,
        });
      }

      // Interest Rating
      if (item.clientCallInterested !== undefined && item.clientCallInterested !== null) {
        fields.push({
          label: 'Interest',
          value: <RatingDisplay value={item.clientCallInterested} />,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          ),
        });
      }

      // Call Date
      if (item.clientCallDate) {
        fields.push({
          label: 'Call Date',
          value: formatDateGlobal(item.clientCallDate),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Created Date
      if (item.clientCallsCreatedAt) {
        fields.push({
          label: 'Created',
          value: formatDateGlobal(item.clientCallsCreatedAt),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      }

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'all',
      options: [
        { value: 'all', label: 'All Calls' },
        { value: 'email_sent', label: 'Email Sent' },
        { value: 'no_email', label: 'No Email' },
      ],
      getStatus: (item: Call) => (item.clientCallSendEmail ? 'email_sent' : 'no_email'),
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updateCall } = await import('../../services/mutations');
      await updateCall(id, { clientCallSendEmail: newStatus === 'email_sent' });
    },
    renderStatusSelector: (item: Call, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      const callStatusOptions = [
        { value: 'no_email', label: 'No Email' },
        { value: 'email_sent', label: 'Email Sent' },
      ];
      return (
        <Select
          value={item.clientCallSendEmail ? 'email_sent' : 'no_email'}
          onChange={(e: any) => onStatusChange(e.target.value)}
          disabled={isUpdating}
          className="text-xs"
          showClearButton={false}
          options={callStatusOptions}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
      delete: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Call Details',
    notFoundTitle: 'Call not found',
    notFoundMessage: 'The call you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Calls',
    defaultTitle: 'Unnamed Call',
    getTitle: (item: Call) => {
      return item.clientCallDescr || `Call #${item.clientCallId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Call',
      subtitle: 'Add a new call to your database',
      submitText: 'Create Call',
    },
    edit: {
      title: 'Edit Call',
      submitText: 'Update Call',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading call...',
    errorMessage: 'Failed to save call. Please try again.',
    create: async (data: Partial<Call>) => {
      const { createCall } = await import('../../services/mutations');
      return await createCall(data);
    },
    update: async (id: number, data: Partial<Call>) => {
      const { updateCall } = await import('../../services/mutations');
      return await updateCall(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
    },
    {
      name: 'clientCallTypeId',
      label: 'Call Type',
      type: 'select',
      required: true,
      placeholder: 'Select call type',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/call-type', // API endpoint to fetch all call type options
      labelField: 'call_type_desc', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'calledBy',
      label: 'Called By',
      type: 'select',
      required: true,
      placeholder: 'Select user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'clientCallDate',
      label: 'Call Date & Time',
      type: 'datetime',
      required: true,
      placeholder: 'Select call date and time',
      validation: {
        required: true,
      },
    },
    {
      name: 'clientCallInterested',
      label: 'Interest Rating',
      type: 'rating',
      precision: 0.5,
      maxRating: 5,
      showLabel: true,
    },
    {
      name: 'clientCallDescr',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter call description',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'clientCallNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter call notes',
      rows: 5,
      validation: {
        maxLength: 5000,
      },
    },
    {
      name: 'clientCallSendEmail',
      label: 'Send Email',
      type: 'switch',
      switchLabel: 'Send email notification',
      description: 'Check if email should be sent',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'clientColor',
      label: 'Color',
      type: 'color',
      presetColors: ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#607d8b'],
    },
  ] as FieldConfig[],

  // Form Layout Configuration
  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📞',
        gridCols: 'md:grid-cols-2',
        fields: ['clientId', 'clientCallTypeId', 'calledBy', 'clientCallDate'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Call Details',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientCallInterested', 'clientCallDescr', 'clientCallNotes', 'clientCallSendEmail', 'clientColor'],
        fieldSpans: {
          clientCallInterested: 'md:col-span-6',
          clientCallDescr: 'md:col-span-12',
          clientCallNotes: 'md:col-span-12',
          clientCallSendEmail: 'md:col-span-6',
          clientColor: 'md:col-span-6',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<Call>[] => [
  {
    key: 'clientCallType',
    header: 'Type',
    sortable: true,
    render: (value, row) => {
      const callType = row.clientCallType?.call_type_desc || '';
      const isOutgoing = callType.toLowerCase().includes('outgoing');
      return (
        <div className="flex items-center justify-center">
          {isOutgoing ? (
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 3h5m0 0v5m0-5l-6 6M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.84 3.681a11.042 11.042 0 01-5.516-5.517l3.681-1.84a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          ) : callType ? (
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.517l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          ) : null}
        </div>
      );
    },
  },
  {
    key: 'client.clientName',
    header: 'Client',
    sortable: true,
    render: (value, row) => {
      const clientName = row.client?.clientName || '-';
      if (clientName === '-') return clientName;

      const displayName =
        clientName.length > 50 ? clientName.substring(0, 50) + '...' : clientName;
      const email = row.client?.clientEmail?.trim();
      const phone = row.client?.clientPhone?.trim();

      return (
        <div className="min-w-0">
          <div>{displayName}</div>
          {(email || phone) && (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {email && (
                <span className="inline-flex items-center gap-1 min-w-0">
                  <svg
                    className="h-3 w-3 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="truncate">{email}</span>
                </span>
              )}
              {phone && (
                <span className="inline-flex items-center gap-1">
                  <svg
                    className="h-3 w-3 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.517l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>{phone}</span>
                </span>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'calledBy',
    header: 'Called By',
    sortable: true,
    render: (value, row) => (
      row.calledByUser?.userDisplayName || row.calledByUser?.userFirstName || (value ? `User #${value}` : '-')
    ),
  },
  {
    key: 'clientCallDate',
    header: 'Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'clientCallDescr',
    header: 'Description',
    sortable: true,
    render: (value) => {
      if (!value) return '-';
      return value.length > 50 ? value.substring(0, 50) + '...' : value;
    },
  },
  {
    key: 'clientCallInterested',
    header: 'Interest',
    sortable: true,
    render: (value) => {
      if (value === undefined || value === null) return '-';
      return <RatingView value={value} precision={0.5} max={5} size="sm" />;
    },
  },
  {
    key: 'clientCallSendEmail',
    header: 'Email',
    sortable: true,
    render: (value) => value ? (
      <svg className="w-5 h-5 text-green-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : null,
  },
  {
    key: 'clientCallsCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Call,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📞',
      fields: [
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'Call Type',
          value: item.clientCallType?.call_type_desc || (item.clientCallTypeId ? `Call Type #${item.clientCallTypeId}` : '-'),
        },
        {
          label: 'Called By',
          value: item.calledByUser
            ? (item.calledByUser.userDisplayName || item.calledByUser.userFirstName || `User #${item.calledByUser.id}`)
            : (item.calledBy ? `User #${item.calledBy}` : 'Unassigned'),
        },
        {
          label: 'Call Date',
          value: formatDate(item.clientCallDate),
        },
        {
          label: 'Interest Rating',
          value: item.clientCallInterested !== undefined && item.clientCallInterested !== null ? (
            <div className="flex items-center gap-2">
              <RatingView value={item.clientCallInterested} precision={0.5} max={5} size="default" showText={true} />
            </div>
          ) : '-',
        },
      ],
    },
    {
      title: 'Call Details',
      icon: '📋',
      fields: [
        {
          label: 'Description',
          value: item.clientCallDescr || '-',
        },
        {
          label: 'Notes',
          value: item.clientCallNotes || '-',
        },
        {
          label: 'Send Email',
          value: item.clientCallSendEmail ? 'Yes' : 'No',
        },
        {
          label: 'Color',
          value: item.clientColor ? (
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded border border-gray-300"
                style={{ backgroundColor: item.clientColor }}
              />
              <span>{item.clientColor}</span>
            </div>
          ) : '-',
        },
      ],
    },
    {
      title: 'System Information',
      icon: '⚙️',
      fields: [
        {
          label: 'Created At',
          value: formatDate(item.clientCallsCreatedAt),
        },
        {
          label: 'Created By',
          value: item.clientCallsCreatedUser?.userFirstName || (item.clientCallsCreatedBy ? `User #${item.clientCallsCreatedBy}` : '-'),
        },
        {
          label: 'Updated At',
          value: formatDate(item.clientCallsUpdatedAt),
        },
        {
          label: 'Updated By',
          value: item.clientCallsUpdatedUser?.userFirstName || (item.clientCallsUpdatedBy ? `User #${item.clientCallsUpdatedBy}` : '-'),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: Call) => {
  return (
    <>
      {item.clientCallSendEmail && (
        <Badge variant="success">Email Sent</Badge>
      )}
    </>
  );
};

// Helper function to get toolbar icons
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to get table actions
export const getTableActions = (
  item: Call,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.clientCallId)}
      editTo={moduleConfig.module.route.edit(item.clientCallId)}
      onDelete={() => onDelete(item.clientCallId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Call> => {
  return {
    clientId: undefined,
    calledBy: undefined,
    clientCallTypeId: undefined,
    clientCallDate: undefined,
    clientCallDescr: '',
    clientCallNotes: '',
    clientCallInterested: 0,
    clientCallSendEmail: false,
    clientColor: '#000000', // Default to black
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Call): Partial<Call> => {
  return {
    clientId: item.clientId,
    calledBy: item.calledBy,
    clientCallTypeId: item.clientCallTypeId,
    clientCallDate: item.clientCallDate,
    clientCallDescr: item.clientCallDescr || '',
    clientCallNotes: item.clientCallNotes || '',
    clientCallInterested: item.clientCallInterested ?? 0,
    clientCallSendEmail: item.clientCallSendEmail ?? false,
    clientColor: item.clientColor,
  };
};

